import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys, creditMemoKeys, invoiceKeys, jobKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';
import { normalizeInvoice } from './useInvoice';
import type { Invoice } from '@/types/invoice';

type VoidInvoiceInput = string | { id: string; reason: string };

export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useApiMutation<Invoice, VoidInvoiceInput>({
    mutationFn: async (input) => {
      const id = typeof input === 'string' ? input : input.id;
      const reason = typeof input === 'string' ? 'Voided from invoice actions' : input.reason;
      return normalizeInvoice(
        await apiPost<Record<string, unknown>, { reason: string }>(
          operationsClient,
          invoiceEndpoints.void(id),
          { reason },
        ),
      );
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Invoice>(invoiceKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      if (data.jobId) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(data.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(data.jobId) });
      }
      // Voiding hands back any credit-memo credit applied to the invoice, so
      // the memos, the customer's available credit and the "Apply memo"
      // suggestions on their other invoices all change with it.
      void queryClient.invalidateQueries({ queryKey: creditMemoKeys.all() });
      if (data.contactId) {
        void queryClient.invalidateQueries({ queryKey: contactKeys.detail(data.contactId) });
      }
      toast.success('Invoice voided');
    },
  });
}
